import { api } from './api.js';
import { transformColor, dtcgToCssVars } from '@elmes/shared';
import type {
  UIToken,
  UITokenMode,
  UITokenPreset,
  UITokenPresetData,
  UIBuildLogEntry,
  UISettings,
  UIComponentFile,
} from '@elmes/shared';

export interface ApiOk<T> {
  success: boolean;
  data: T;
}

export const uiSystemService = {
  async getTokens(mode?: UITokenMode | 'all') {
    const res = await api.get<ApiOk<UIToken[]>>('/ui-system/tokens', {
      params: mode ? { mode } : {},
    });
    return res.data.data;
  },

  async updateTokens(tokens: Array<{ key: string; mode?: UITokenMode; value: string }>) {
    const res = await api.put<ApiOk<{ updated: number; keys: string[] }>>('/ui-system/tokens', {
      tokens,
    });
    return res.data.data;
  },

  async resetTokens(payload: { mode?: UITokenMode; keys?: string[] }) {
    const res = await api.post<ApiOk<{ reset: number }>>('/ui-system/tokens/reset', payload);
    return res.data.data;
  },

  async updateDefaults(
    tokens: Array<{ key: string; mode?: UITokenMode; defaultValue: string }>,
  ) {
    const res = await api.put<ApiOk<{ updated: number }>>('/ui-system/tokens/defaults', {
      tokens,
    });
    return res.data.data;
  },

  async exportTokens(): Promise<UITokenPresetData & { exportedAt: string }> {
    const res = await api.get('/ui-system/tokens/export', { responseType: 'text' });
    return typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
  },

  async importTokens(payload: UITokenPresetData) {
    const res = await api.post<ApiOk<{ updated: number }>>('/ui-system/tokens/import', payload);
    return res.data.data;
  },

  async listPresets() {
    const res = await api.get<ApiOk<UITokenPreset[]>>('/ui-system/tokens/presets');
    return res.data.data;
  },

  async getPreset(id: number) {
    const res = await api.get<ApiOk<UITokenPreset>>(`/ui-system/tokens/presets/${id}`);
    return res.data.data;
  },

  async createPreset(input: { name: string; description?: string; fromCurrent?: boolean; data?: UITokenPresetData }) {
    const res = await api.post<ApiOk<{ id: number }>>('/ui-system/tokens/presets', input);
    return res.data.data;
  },

  async applyPreset(id: number) {
    const res = await api.post<ApiOk<{ updated: number }>>(`/ui-system/tokens/presets/${id}/apply`);
    return res.data.data;
  },

  async deletePreset(id: number) {
    const res = await api.delete<ApiOk<{ deleted: boolean }>>(`/ui-system/tokens/presets/${id}`);
    return res.data.data;
  },

  async listComponents() {
    const res = await api.get<ApiOk<UIComponentFile[]>>('/ui-system/components');
    return res.data.data;
  },

  async readComponent(path: string) {
    const res = await api.get<ApiOk<{ path: string; content: string }>>(
      '/ui-system/components/content',
      { params: { path } },
    );
    return res.data.data;
  },

  async readBackup(path: string) {
    const res = await api.get<ApiOk<{ path: string; content: string }>>(
      '/ui-system/components/backup',
      { params: { path } },
    );
    return res.data.data;
  },

  async writeComponent(
    path: string,
    content: string,
    opts?: { startLine?: number; endLine?: number },
  ) {
    const res = await api.put<ApiOk<{ path: string; buildTriggered: boolean }>>(
      '/ui-system/components/content',
      { path, content, ...opts },
    );
    return res.data.data;
  },

  async triggerBuild() {
    const res = await api.post<ApiOk<{ id: number; status: 'running' }>>('/ui-system/build/trigger');
    return res.data.data;
  },

  async getBuildStatus() {
    const res = await api.get<ApiOk<UIBuildLogEntry | null>>('/ui-system/build/status');
    return res.data.data;
  },

  async getBuildLogs(limit = 10) {
    const res = await api.get<ApiOk<UIBuildLogEntry[]>>('/ui-system/build/logs', {
      params: { limit },
    });
    return res.data.data;
  },

  async getSettings() {
    const res = await api.get<ApiOk<UISettings>>('/ui-system/settings');
    return res.data.data;
  },

  async updateSettings(data: Partial<UISettings>) {
    const res = await api.put<ApiOk<UISettings>>('/ui-system/settings', data);
    return res.data.data;
  },
};

/**
 * Color CSS-var keys (the 17 shadcn semantic color tokens) whose values are
 * stored in the DB as HSL-bare channels (e.g. `221.2 83.2% 53.3%`) but must be
 * injected as oklch channels so the Tailwind preset can wrap them as
 * `oklch(var(--x) / <alpha-value>)`. Non-color tokens (e.g. `--radius`,
 * `--ui-contrast`, `--ui-brightness`) are injected verbatim.
 *
 * Kept in sync with `@elmes/shared` TOKEN_MAP (17 color keys). We gate on a
 * Set rather than `transformColor`'s success so that visual-adjustment scalars
 * (`--ui-contrast: 1`) are never mistaken for colors.
 */
const COLOR_TOKEN_KEYS: ReadonlySet<string> = new Set([
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--primary',
  '--primary-foreground',
  '--secondary',
  '--secondary-foreground',
  '--muted',
  '--muted-foreground',
  '--accent',
  '--accent-foreground',
  '--destructive',
  '--destructive-foreground',
  '--border',
  '--input',
  '--ring',
]);

/**
 * Resolve a DB token value to the string injected into the CSS custom property.
 *
 * Color tokens (HSL-bare in the DB, legacy seed preserved — no migration) are
 * transformed to **bare oklch channels** (`L C H`, without the `oklch(...)`
 * wrapper) via the shared, culori-backed `transformColor`. Bare channels are
 * required because the Tailwind preset composes them as
 * `oklch(var(--token) / <alpha-value>)`; a wrapped value would yield the
 * invalid `oklch(oklch(...) / a)`.
 *
 * The transform is perceptual-equivalent to the source HSL (culori preserves
 * sRGB), so render output is unchanged versus the previous `hsl(var(--x))`
 * pipeline. Non-color tokens and any value that fails to parse fall back to the
 * raw value (no silent loss of `--radius`, custom scalars, etc.).
 */
export function resolveTokenCssValue(key: string, value: string): string {
  if (!COLOR_TOKEN_KEYS.has(key)) return value;
  const oklch = transformColor(value, 'oklch'); // -> "oklch(L C H)" | undefined
  if (!oklch) return value; // unparseable → preserve raw, never drop
  // Strip the `oklch(` prefix and trailing `)` to expose bare channels for the
  // preset's `oklch(var(--x) / <alpha-value>)` composition.
  const inner = oklch.slice('oklch('.length, -1).trim();
  return inner.length > 0 ? inner : value;
}

/**
 * Inject tokens as CSS custom properties on :root.
 * For light mode tokens they go on `:root`; dark mode values are injected under `.dark` via a style tag.
 *
 * Color tokens are run through {@link resolveTokenCssValue} (HSL-bare → oklch
 * channels). DB-driven live editing is preserved: re-invoking with updated
 * tokens re-applies the latest values (TokensTab → PUT → loadAndApplyTokens).
 */
export function applyTokensToDOM(tokens: UIToken[]) {
  const root = document.documentElement;
  const light = tokens.filter((t) => t.mode === 'light');
  const dark = tokens.filter((t) => t.mode === 'dark');

  for (const token of light) {
    root.style.setProperty(token.key, resolveTokenCssValue(token.key, token.value));
  }

  // Dark mode values via a managed <style> tag so `.dark` selector picks them up
  let styleEl = document.getElementById('elmes-dark-tokens') as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'elmes-dark-tokens';
    document.head.appendChild(styleEl);
  }
  if (dark.length > 0) {
    styleEl.textContent = `.dark {\n${dark
      .map((t) => `  ${t.key}: ${resolveTokenCssValue(t.key, t.value)};`)
      .join('\n')}\n}`;
  } else {
    styleEl.textContent = '';
  }
}

export async function loadAndApplyTokens() {
  try {
    const tokens = await uiSystemService.getTokens('all');
    applyTokensToDOM(tokens);
  } catch {
    // Unauthenticated or API unreachable — use CSS defaults from index.css
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Active-brand theming cascade (Faz-5 PR-5, roadmap #441, umbrella #421).
//
// Cascade strategy "A" (brand inline, last-write-wins): PR-3's default-instance
// pipeline (`applyTokensToDOM`) writes the chrome defaults as INLINE styles on
// `:root` (`documentElement.style`). An inline `:root` style wins over ANY
// stylesheet rule — including a `[data-brand="…"]` selector — so a brand theme
// must ALSO be written inline to override the defaults. We therefore reuse the
// EXACT same mechanism as PR-3 (`root.style.setProperty`) for brand tokens, and
// rely on later-write-wins on the same property.
//
// `applyTokensToDOM` (PR-3, deployed + tested) is intentionally NOT touched —
// the brand layer is additive and isolated.
// ─────────────────────────────────────────────────────────────────────────────

/** The active-brand payload from `GET /api/entity/active-brand`. */
export interface ActiveBrand {
  brandId: number;
  slug: string;
  name: string;
  /** W3C DTCG token bag (the active design system's snapshot). */
  designTokens: Record<string, unknown>;
}

/**
 * localStorage key for the FOUC cache: the LAST-applied brand's already-resolved
 * `:root` CSS-var map (`{ slug, vars }`), so the blocking inline script in
 * index.html can paint the brand on the FIRST frame — before React boots and
 * re-resolves from the server. Pure presentation cache; the server remains the
 * source of truth (boot re-resolve overwrites it). Kept in sync ONLY here.
 */
const BRAND_FOUC_CACHE_KEY = 'elmes-brand-fouc';

/**
 * Resolve a brand's DTCG bag to the EXACT `{ cssVar: injectedValue }` map written
 * to `:root` — the single computation shared by the live DOM apply and the FOUC
 * cache, so the pre-paint frame and the React frame are byte-identical.
 *
 * The DTCG bag is run through the SAME shared transform PR-1 exposes
 * (`dtcgToCssVars`, target `oklch`) — the single source of truth for DTCG →
 * shadcn CSS-var mapping. Unknown / missing DTCG paths are silently dropped by
 * the transform (only resolvable known keys appear), so a partial brand bag
 * yields only the tokens it actually carries.
 *
 * Channel shape: `dtcgToCssVars` emits WRAPPED color values (`oklch(L C H)`) but
 * the Tailwind preset composes `oklch(var(--token) / <alpha-value>)`, so color
 * tokens are emitted as BARE channels (`L C H`). The wrapper is stripped for the
 * known color keys (the SAME `COLOR_TOKEN_KEYS` gate `applyTokensToDOM` uses);
 * non-color tokens (`--radius`) pass through verbatim.
 */
export function resolveBrandCssVars(
  designTokens: Record<string, unknown>,
): Record<string, string> {
  const cssVars = dtcgToCssVars(designTokens, { target: 'oklch' });
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(cssVars)) {
    if (COLOR_TOKEN_KEYS.has(key)) {
      out[key] = value.startsWith('oklch(')
        ? value.slice('oklch('.length, -1).trim()
        : value;
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Apply a brand's DTCG design tokens to the live DOM as INLINE `:root` custom
 * properties (cascade "A": last-write-wins over the PR-3 default instance) using
 * the SAME mechanism as PR-3 (`root.style.setProperty`).
 */
export function applyBrandTokensToDOM(designTokens: Record<string, unknown>): void {
  const root = document.documentElement;
  const vars = resolveBrandCssVars(designTokens);
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}

/** Persist / clear the FOUC cache (the last brand's resolved `:root` vars). */
function writeBrandFoucCache(brand: ActiveBrand | null): void {
  try {
    if (brand) {
      const vars = resolveBrandCssVars(brand.designTokens);
      localStorage.setItem(
        BRAND_FOUC_CACHE_KEY,
        JSON.stringify({ slug: brand.slug, vars }),
      );
    } else {
      localStorage.removeItem(BRAND_FOUC_CACHE_KEY);
    }
  } catch {
    // localStorage unavailable / quota — FOUC cache is best-effort only.
  }
}

/**
 * Set / clear the active-brand DOM marker (`<html data-brand="slug">`).
 *
 * Purely a marker — the actual theming is the inline `:root` properties written
 * by {@link applyBrandTokensToDOM}. The attribute exists for the FOUC inline
 * script, analytics, and a future pure-cascade (selector-based "B") migration.
 */
function setBrandMarker(slug: string | null): void {
  if (slug) {
    document.documentElement.dataset.brand = slug;
  } else {
    delete document.documentElement.dataset.brand;
  }
}

/**
 * Make `brand` the active theme — or, when `brand` is `null`, return to the
 * PR-3 default instance (brand off / switched away).
 *
 * Clean reset on every transition (the load-bearing cascade-"A" rule): because
 * brand tokens are written as inline `:root` overrides, switching brands or
 * turning a brand off must FIRST restore the PR-3 defaults (re-fetch + re-apply
 * the default instance, clearing stale brand inlines on the shared properties)
 * and only THEN write the new brand's inlines. Without the reset, a property the
 * NEW brand doesn't set would keep the OLD brand's value (inline styles are not
 * auto-cleared). `loadAndApplyTokens()` is the authoritative default restore.
 */
export async function applyActiveBrand(brand: ActiveBrand | null): Promise<void> {
  // 1. Restore PR-3 defaults (clears any prior brand's inline overrides).
  await loadAndApplyTokens();
  // 2. Layer the new brand inline (last-write-wins), or leave defaults if off.
  if (brand) {
    applyBrandTokensToDOM(brand.designTokens);
    setBrandMarker(brand.slug);
  } else {
    setBrandMarker(null);
  }
  // 3. Refresh the FOUC cache so the next first-paint matches this state.
  writeBrandFoucCache(brand);
}
